import { useEffect, useState } from "react";
import assignmentsStyles from './styles/assignment.module.scss';
import { getDates } from "@/utils/dateFunctions";
import { AssignmentHeader } from "./components/AssignmentHeader";
import { AssignmentTable } from "./components/AssignmentTable/AssignmentTable";
import { useGetTaskAssignmentsWithSchedules } from "./hooks/timeAssignments/useGetTaskAssignmentsWithSchedules";
import { CircularLoader } from "@/components/Layout/CircularLoader";
import { IListTimeAssignmentsResponse, Assignment } from "@/api/timeAssignments/types";
import { TaskDetailDrawer } from "@/components/common/TaskDetailDrawer";
import { ApiStatusIndicator } from "@/components/common/ApiStatusIndicator";
import { NetworkDiagnostic } from "@/components/common/NetworkDiagnostic";
import { logComponentState } from "@/utils/debug";

interface FormattedAssignment extends IListTimeAssignmentsResponse {
  hoursWeek: { date: string; realh: number; estimatedh: number }[];
  totale: number;
  totalr: number;
}

export const Assignments = () => {
  const [dateFrom, setDateFrom] = useState(getDates().dateFrom);
  const [dateTo, setDateTo] = useState(getDates().dateTo);
  const [week, setWeek] = useState(getDates().week);
  const [weekNumber, setWeekNumber] = useState(1);
  const [teamDayTotal, setTeamDayTotal] = useState([0, 0, 0, 0, 0, 0, 0]);
  const [teamTotalR, setTeamTotalR] = useState<number>(0);
  const [teamTotalE, setTeamTotalE] = useState<number>(0);
  const [range, setRange] = useState(getDates().range);
  const [showDetail, setShowDetail] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const { data, isLoading, error, refetch: refetchTasksAssignments } = useGetTaskAssignmentsWithSchedules(week[0], week[week.length - 1]);

  // Debug logging
  useEffect(() => {
    logComponentState('Assignments', {
      week: week[0] + ' to ' + week[week.length - 1],
      dataLength: Array.isArray(data) ? data.length : 'not array',
      isLoading,
      error: error?.message
    });
  }, [data, isLoading, error, week]);

  useEffect(() => {
    if (Array.isArray(data)) {
      formatInfo(data, week, setTeamDayTotal, setTeamTotalR, setTeamTotalE);
    }
  }, [data, week, setTeamDayTotal, setTeamTotalR, setTeamTotalE]);

  const handleShowTaskDetail = (task: Assignment) => {
    setSelectedTaskId(task.task_id?.toString() || '');
    setShowDetail(true);
  };

  const handleCloseDrawer = () => {
    setShowDetail(false);
    setSelectedTaskId(null);
  };

  function formatInfo(
    dataInit: IListTimeAssignmentsResponse[],
    week: string[],
    setTeamDayTotal: (arr: number[]) => void,
    setTeamTotalR: (val: number) => void,
    setTeamTotalE: (val: number) => void
  ) {
    const data: FormattedAssignment[] = dataInit.map(item => ({
      ...item,
      hoursWeek: [],
      totale: 0,
      totalr: 0,
    }));

    data.sort((a, b) => a.fullName.localeCompare(b.fullName));

    data.forEach((item) => {
      const hoursWeek = week.map((weekDate) => ({
        date: weekDate,
        realh: 0,
        estimatedh: 0,
      }));

      item.assignments?.forEach((assignment: Assignment) => {
        assignment.schedules?.forEach((schedule) => {
          const dateSet = schedule.scheduleDate.substring(0, 10);
          const hour = hoursWeek.find((h) => h.date === dateSet);
          if (hour) {
            hour.realh += Number(schedule.real_time) || 0;
            hour.estimatedh += Number(schedule.estimated_time) || 0;
          }
        });
      });

      item.hoursWeek = hoursWeek;
      item.totale = hoursWeek.reduce((sum, hour: { estimatedh: number }) => sum + hour.estimatedh, 0);
      item.totalr = hoursWeek.reduce((sum, hour: { realh: number }) => sum + hour.realh, 0);
    });

    const tr = data.reduce((sum, item) => sum + (item.totalr || 0), 0);
    const te = data.reduce((sum, item) => sum + (item.totale || 0), 0);
    const arr = Array(week.length).fill(0);

    data.forEach((item) => {
      item.hoursWeek.forEach((hour: { estimatedh: number }, idx: number) => {
        arr[idx] += hour.estimatedh;
      });
    });

    setTeamDayTotal(arr);
    setTeamTotalR(tr);
    setTeamTotalE(te);
  }

  return (
    <div className={assignmentsStyles.container}>
      <ApiStatusIndicator 
        apiUrl={import.meta.env.VITE_API_SOURCE_URL} 
        serviceName="Servicio de Asignaciones" 
      />
      <div className={assignmentsStyles['content-wrapper']}>
        <div className={assignmentsStyles['table-container']}>
          <AssignmentHeader
            range={range}
          />
          {isLoading && <CircularLoader />}
          {error && (
            <div style={{ padding: '20px', textAlign: 'center' }}>
              <h3 style={{ color: '#dc3545' }}>Error al cargar asignaciones</h3>
              <p style={{ color: '#6c757d', marginBottom: '20px' }}>
                {error.message || 'No se pudieron cargar las asignaciones'}
              </p>
              <button 
                onClick={() => refetchTasksAssignments()}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Intentar de nuevo
              </button>
              <details style={{ marginTop: '20px', textAlign: 'left' }}>
                <summary style={{ cursor: 'pointer' }}>Ver detalles del error</summary>
                <pre style={{ 
                  backgroundColor: '#f8f9fa', 
                  padding: '10px', 
                  borderRadius: '4px',
                  fontSize: '12px',
                  overflow: 'auto',
                  marginTop: '10px'
                }}>
                  {JSON.stringify(error, null, 2)}
                </pre>
              </details>
            </div>
          )}
          {!isLoading && !error && (
            <>
              {Array.isArray(data) && data.length === 0 ? (
                <div style={{ 
                  padding: '40px 20px', 
                  textAlign: 'center',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  margin: '20px 0'
                }}>
                  <h3 style={{ color: '#6c757d', marginBottom: '15px' }}>
                    📋 No hay asignaciones disponibles
                  </h3>
                  <p style={{ color: '#6c757d', marginBottom: '20px' }}>
                    No se encontraron asignaciones para esta semana, o el servicio no está disponible temporalmente.
                  </p>
                  <button 
                    onClick={() => refetchTasksAssignments()}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    🔄 Reintentar
                  </button>
                  <div style={{ 
                    marginTop: '20px', 
                    padding: '15px',
                    backgroundColor: '#e3f2fd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    color: '#1976d2'
                  }}>
                    💡 <strong>Nota:</strong> La aplicación sigue funcionando. 
                    Las asignaciones se mostrarán cuando el servicio esté disponible.
                  </div>
                </div>
              ) : (
                <AssignmentTable
                  setRange={setRange}
                  week={week}
                  getInfo={refetchTasksAssignments}
                  assignmentsData={Array.isArray(data) ? data : []}
                  weeknumber={weekNumber}
                  teamDayTotal={teamDayTotal}
                  teamTotalR={teamTotalR}
                  teamTotalE={teamTotalE}
                  dateFrom={dateFrom}
                  dateTo={dateTo}
                  setDateFrom={setDateFrom}
                  setDateTo={setDateTo}
                  setWeek={setWeek}
                  setWeekNumber={setWeekNumber}
                  actionShowTaskDetail={(_, task) => handleShowTaskDetail(task)}
                />
              )}
            </>
          )}
        </div>
      </div>
      <TaskDetailDrawer
        isOpen={showDetail}
        taskId={selectedTaskId}
        onClose={handleCloseDrawer}
      />
      <NetworkDiagnostic show={process.env.NODE_ENV === 'development'} />
    </div>
  );
};

export default Assignments;
